import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import Footer from "../footer/Footer";
import GdCommentThread from "./GdCommentThread";
import { fetchClientGdTopicById } from "../../services/clientGdService";
import TopicContentRenderer from "../study/topic-content/TopicContentRenderer";
import "./GdDetailPage.css";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const DIFF_COLORS = { BEGINNER: "#4caf50", INTERMEDIATE: "#ff9800", ADVANCED: "#e91e63" };
const DIFF_BG    = { BEGINNER: "#f1faf1", INTERMEDIATE: "#fff8f0", ADVANCED: "#fff0f4" };

const getEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([-\w]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return null;
};

const Section = ({ title, children, accent = "#019095" }) => (
  <div style={{
    background: "#fff",
    border: "1px solid #eef0f3",
    borderRadius: 16,
    padding: "28px 32px",
    marginBottom: 24,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  }}>
    <h3 style={{
      fontSize: 20,
      fontWeight: 700,
      marginBottom: 20,
      marginTop: 0,
      color: "#1a1a2e",
      display: "flex",
      alignItems: "center",
      gap: 10,
      paddingBottom: 12,
      borderBottom: `2px solid ${accent}22`,
    }}>{title}</h3>
    {children}
  </div>
);

const GdDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [openAnswer, setOpenAnswer] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await fetchClientGdTopicById(id);
      if (res.status === 200 && res.data) setData(res.data.data || res.data);
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return (
    <>
      <Navbar />
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ fontSize: 18, color: "#888" }}>Loading...</p>
      </div>
      <Footer />
    </>
  );

  if (!data) return (
    <>
      <Navbar />
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
        <p style={{ fontSize: 18, color: "#555" }}>Topic not found.</p>
        <button onClick={() => navigate("/gd")} style={{ padding: "10px 24px", background: "#019095", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 16 }}>← Back to Topics</button>
      </div>
      <Footer />
    </>
  );

  const isComingSoon = data.status === "COMING_SOON";

  return (
    <>
      <Navbar />

      {/* Hero banner */}
      <div style={{
        background: data.category?.color
          ? `linear-gradient(135deg, ${data.category.color}18 0%, #f8fafc 100%)`
          : "linear-gradient(135deg, #e8f5f5 0%, #f8fafc 100%)",
        borderBottom: "1px solid #eef0f3",
        padding: "40px 16px 32px",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <button
            onClick={() => navigate("/gd")}
            style={{ background: "none", border: "none", color: "#019095", cursor: "pointer", fontSize: 15, padding: 0, marginBottom: 20, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}
          >
            ← Back to Topics
          </button>

          {/* Category + badges */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            {data.category && (
              <span style={{
                fontSize: 13, fontWeight: 700,
                color: data.category.color,
                background: data.category.color + "20",
                border: `1px solid ${data.category.color}44`,
                borderRadius: 999, padding: "4px 14px",
              }}>
                {data.category.name}
              </span>
            )}
            <span style={{
              fontSize: 13, fontWeight: 700,
              color: DIFF_COLORS[data.difficulty],
              background: DIFF_BG[data.difficulty],
              border: `1px solid ${DIFF_COLORS[data.difficulty]}44`,
              borderRadius: 999, padding: "4px 14px",
            }}>
              {data.difficulty}
            </span>
          </div>

          <h1 style={{ fontSize: 34, fontWeight: 800, color: "#1a1a2e", margin: "0 0 20px", lineHeight: 1.25 }}>
            {data.title}
          </h1>

          {/* Thumbnail below title */}
          {(data.thumbnailUrl || data.category?.coverImageUrl) && (
            <div style={{ borderRadius: 14, overflow: "hidden", maxHeight: 360 }}>
              <img
                src={`${BASE_ROOT}${data.thumbnailUrl || data.category?.coverImageUrl}`}
                alt={data.title}
                style={{ width: "100%", maxHeight: 360, objectFit: "cover", display: "block" }}
              />
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px 48px" }}>

        {/* Coming Soon */}
        {isComingSoon && (
          <div style={{ textAlign: "center", padding: "80px 20px", background: "#fff", borderRadius: 16, border: "1px solid #eef0f3", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🔒</div>
            <h2 style={{ fontSize: 24, color: "#444", marginBottom: 10 }}>Content Coming Soon</h2>
            <p style={{ fontSize: 17, color: "#888" }}>This topic is being prepared. Check back later!</p>
          </div>
        )}

        {!isComingSoon && (
          <>
            {/* Image carousel */}
            {data.images?.length > 0 && (
              <Section title="📸 Images">
                <div style={{ textAlign: "center" }}>
                  <img
                    src={`${BASE_ROOT}${data.images[imgIdx].url}`}
                    alt={data.images[imgIdx].altText || data.title}
                    style={{ maxWidth: "100%", maxHeight: 380, objectFit: "contain", borderRadius: 10 }}
                  />
                  {data.images.length > 1 && (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 14 }}>
                      <button onClick={() => setImgIdx((i) => Math.max(0, i - 1))} disabled={imgIdx === 0}
                        style={{ padding: "6px 18px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", cursor: "pointer", fontSize: 16 }}>←</button>
                      <span style={{ fontSize: 15, color: "#666" }}>{imgIdx + 1} / {data.images.length}</span>
                      <button onClick={() => setImgIdx((i) => Math.min(data.images.length - 1, i + 1))} disabled={imgIdx === data.images.length - 1}
                        style={{ padding: "6px 18px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", cursor: "pointer", fontSize: 16 }}>→</button>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* Description */}
            {data.description && (
              <Section title="📝 Overview">
                <div
                  dangerouslySetInnerHTML={{ __html: data.description }}
                  style={{ fontSize: 17, lineHeight: 1.85, color: "#2d2d2d" }}
                  className="gd-html-content"
                />
              </Section>
            )}

            {/* Key Points */}
            {data.keyPoints?.length > 0 && (
              <Section title="🔑 Key Points" accent="#f59e0b">
                <ol style={{ paddingLeft: 0, margin: 0, listStyle: "none" }}>
                  {data.keyPoints.map((kp, idx) => (
                    <li key={kp.id} style={{
                      display: "flex", gap: 14, alignItems: "flex-start",
                      padding: "12px 0",
                      borderBottom: idx < data.keyPoints.length - 1 ? "1px solid #f5f5f5" : "none",
                    }}>
                      <span style={{
                        minWidth: 30, height: 30, borderRadius: "50%",
                        background: "#f59e0b", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 700, fontSize: 14, flexShrink: 0,
                      }}>{idx + 1}</span>
                      <span style={{ fontSize: 17, color: "#2d2d2d", lineHeight: 1.6, paddingTop: 4 }}>{kp.point}</span>
                    </li>
                  ))}
                </ol>
              </Section>
            )}

            {/* Dos & Don'ts */}
            {data.dosAndDonts?.length > 0 && (
              <Section title="✅ Do's & ❌ Don'ts">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  {["DO", "DONT"].map((type) => (
                    <div key={type} style={{
                      background: type === "DO" ? "#f1faf1" : "#fff5f5",
                      borderRadius: 12, padding: "18px 20px",
                      border: `1px solid ${type === "DO" ? "#c8e6c9" : "#ffcdd2"}`,
                    }}>
                      <h4 style={{ color: type === "DO" ? "#2e7d32" : "#c62828", marginBottom: 14, fontSize: 16, fontWeight: 700 }}>
                        {type === "DO" ? "✅ Do's" : "❌ Don'ts"}
                      </h4>
                      <ul style={{ paddingLeft: 0, margin: 0, listStyle: "none" }}>
                        {data.dosAndDonts.filter((d) => d.type === type).map((d) => (
                          <li key={d.id} style={{ display: "flex", gap: 10, marginBottom: 10, fontSize: 16, color: "#333", alignItems: "flex-start" }}>
                            <span style={{ color: type === "DO" ? "#4caf50" : "#e53935", fontWeight: 700, flexShrink: 0, marginTop: 1 }}>
                              {type === "DO" ? "✓" : "✗"}
                            </span>
                            {d.item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Sample Answers */}
            {data.sampleAnswers?.length > 0 && (
              <Section title="💡 Sample Answers" accent="#8b5cf6">
                {data.sampleAnswers.map((ans, idx) => (
                  <div key={ans.id} style={{
                    border: "1px solid #ede9fe",
                    borderRadius: 10,
                    marginBottom: 12,
                    overflow: "hidden",
                  }}>
                    <button
                      onClick={() => setOpenAnswer(openAnswer === ans.id ? null : ans.id)}
                      style={{
                        width: "100%", textAlign: "left",
                        padding: "16px 20px",
                        background: openAnswer === ans.id ? "#f5f3ff" : "#faf9ff",
                        border: "none", cursor: "pointer",
                        fontWeight: 700, fontSize: 17,
                        color: "#4c1d95",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}
                    >
                      <span>{ans.title || `Sample Answer ${idx + 1}`}</span>
                      <span style={{ fontSize: 13, color: "#7c3aed" }}>{openAnswer === ans.id ? "▲ Hide" : "▼ Show"}</span>
                    </button>
                    {openAnswer === ans.id && (
                      <div
                        style={{ padding: "20px 24px", borderTop: "1px solid #ede9fe", fontSize: 17, lineHeight: 1.85, color: "#2d2d2d" }}
                        dangerouslySetInnerHTML={{ __html: ans.answer }}
                        className="gd-html-content"
                      />
                    )}
                  </div>
                ))}
              </Section>
            )}

            {/* Vocabulary */}
            {data.vocabularies?.length > 0 && (
              <Section title="📚 Vocabulary" accent="#1565c0">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                  {data.vocabularies.map((v) => (
                    <div
                      key={v.id}
                      onClick={() => navigate(`/study/vocabulary/${v.id}`, { state: { from: "gd", gdId: id } })}
                      style={{
                        border: "1px solid #bbdefb",
                        borderRadius: 10, padding: "14px 18px",
                        background: "#f8fbff", cursor: "pointer",
                        minWidth: 160, maxWidth: 240,
                        transition: "box-shadow 0.15s, transform 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(21,101,192,0.15)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                    >
                      <div style={{ fontWeight: 800, fontSize: 17, color: "#1565c0", marginBottom: 3 }}>{v.word}</div>
                      {v.partOfSpeech && <div style={{ fontSize: 12, color: "#90a4ae", fontStyle: "italic", marginBottom: 6 }}>{v.partOfSpeech}</div>}
                      <div style={{ fontSize: 14, color: "#546e7a", lineHeight: 1.5 }}>{v.definition}</div>
                      {v.examples?.length > 0 && (
                        <div style={{ marginTop: 8, fontSize: 13, color: "#90a4ae", fontStyle: "italic", lineHeight: 1.4 }}>"{v.examples[0]}"</div>
                      )}
                      <div style={{ marginTop: 10, fontSize: 12, color: "#019095", fontWeight: 700 }}>View full details →</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Videos */}
            {data.videos?.length > 0 && (
              <Section title="🎬 Videos" accent="#e53935">
                {data.videos.map((v) => {
                  const embed = getEmbedUrl(v.videoUrl);
                  return (
                    <div key={v.id} style={{ marginBottom: 20 }}>
                      {v.title && <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 17, color: "#1a1a2e" }}>{v.title}</p>}
                      {embed ? (
                        <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: 10 }}>
                          <iframe src={embed} title={v.title} allowFullScreen frameBorder="0"
                            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
                        </div>
                      ) : (
                        <a href={v.videoUrl} target="_blank" rel="noopener noreferrer"
                          style={{ color: "#019095", fontSize: 16, fontWeight: 600 }}>{v.videoUrl}</a>
                      )}
                    </div>
                  );
                })}
              </Section>
            )}

            {/* Comments */}
            <div style={{ background: "#fff", border: "1px solid #eef0f3", borderRadius: 16, padding: "28px 32px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <GdCommentThread topicId={id} />
            </div>

            <TopicContentRenderer topicType="GD_TOPIC" topicId={data.id} />
          </>
        )}
      </div>
      <Footer />
    </>
  );
};

export default GdDetailPage;
